import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, Input, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

import { TranslocoModule } from '@jsverse/transloco';

import { CapitalizePipe } from '@hmax-order/frontend/utils';

/** `type="color"` só aceita hex válido; nome CSS (ex.: `'white'`) vira preto no swatch. Fallback neutro. */
const FALLBACK_SWATCH = '#000000';
const HEX_COLOR_PATTERN = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

/**
 * Cor como texto + swatch nativo (`input type="color"`): o texto aceita qualquer valor CSS que o
 * gerente já use (`white`, `rgb(…)`), e o swatch dá o seletor visual para quem não fala hex.
 * Extraído da tela de Aparência quando a prévia do cardápio precisou dos mesmos campos.
 */
@Component({
  selector: 'hmax-input-color',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './input-color.component.html',
  imports: [CapitalizePipe, CommonModule, MatFormFieldModule, MatInputModule, ReactiveFormsModule, TranslocoModule],
})
export class InputColorComponent implements OnInit {
  private readonly _destroyRef = inject(DestroyRef);

  @Input() controlName!: string;
  @Input() formGroup!: FormGroup;
  @Input() label!: string;
  @Input() placeholder = '';

  /** Sinal, e não getter: com OnPush, `patchValue` de fora não re-renderiza getter — swatch ficava velho. */
  protected readonly swatchColor = signal(FALLBACK_SWATCH);

  get formControl(): AbstractControl | null {
    return this.formGroup.get(this.controlName);
  }

  ngOnInit(): void {
    this.syncSwatch(this.formControl?.value);
    this.formControl?.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((value: string | null) => this.syncSwatch(value));
  }

  /** Aplica a cor escolhida no swatch ao control de texto (e marca dirty, como digitação faria). */
  protected applySwatchColor(value: string): void {
    this.formControl?.setValue(value);
    this.formControl?.markAsDirty();
  }

  private syncSwatch(value: string | null | undefined): void {
    this.swatchColor.set(value && HEX_COLOR_PATTERN.test(value) ? value : FALLBACK_SWATCH);
  }
}
